interface DiscoverHeaderProps {
    count: number;
    categoryTitle?: string;
    toggleLabel?: string;
    /** When true, formats count with "+" suffix even without categoryTitle/toggleLabel */
    formatCount?: boolean;
}

/**
 * Formats a count to show rounded thousands with "+" suffix
 * e.g., 8101 -> "8,000+", 25432 -> "25,000+"
 * For counts under 1000, shows the actual count without "+" suffix
 */
const formatCount = (count: number): string => {
    if (count < 1000) {
        return count.toLocaleString();
    }
    const rounded = Math.floor(count / 1000) * 1000;
    return `${rounded.toLocaleString()}+`;
};

/**
 * Header for the category page that shows the count (rounded to the thousands) of products and the category title.
 * @param count - The count of products.
 * @param categoryTitle - The title of the category.
 * @param toggleLabel - The label of the toggle.
 * @returns
 */
export const DiscoverHeader = ({
    count,
    categoryTitle,
    toggleLabel,
    formatCount: shouldFormatCount,
}: DiscoverHeaderProps) => {
    // Build the display text based on context
    const getDisplayText = () => {
        if (categoryTitle) {
            // First-level category page: "Discover 8,000+ Bathroom Products"
            return `Discover ${formatCount(count)} ${categoryTitle} Products`;
        }
        if (toggleLabel) {
            // Root with toggle: "Discover 25,000+ Interior Products"
            return `Discover ${formatCount(count)} ${toggleLabel} Products`;
        }
        // Root without toggle context or with formatCount prop
        if (shouldFormatCount) {
            return `Discover ${formatCount(count)} Products`;
        }
        return `Discover ${count.toLocaleString()} Products`;
    };

    return (
        <div className="px-4 py-12 md:px-12">
            <hr className="m-0 h-0 border-t border-[#232323] opacity-20" />
            <h2 className="m-0 pt-5 text-5.5 font-medium leading-normal tracking-[-0.22px] text-[#141414]">
                {getDisplayText()}
            </h2>
        </div>
    );
};
